import { useEffect, useState } from 'react';
import { Input, ModalForm } from '@/contasis';
import { formValidation, commonRules, type Validators } from '@/utils';

export interface CatalogoCodigoNombreInput {
  codigo: string;
  nombre: string;
}

interface ModalCatalogoCodigoNombreProps {
  isOpen: boolean;
  title: string;
  codigoLabel?: string;
  nombreLabel?: string;
  onClose: () => void;
  onSubmit: (input: CatalogoCodigoNombreInput) => Promise<void>;
}

const crearValidadoresIniciales = (): Validators => ({
  codigo: formValidation.createFieldValidator([
    commonRules.required('Ingresa el código'),
    commonRules.maxLength(50, 'Máximo 50 caracteres'),
  ]),
  nombre: formValidation.createFieldValidator([
    commonRules.required('Ingresa el nombre'),
    commonRules.maxLength(200, 'Máximo 200 caracteres'),
  ]),
});

export function ModalCatalogoCodigoNombre({
  isOpen,
  title,
  codigoLabel = 'Código',
  nombreLabel = 'Nombre',
  onClose,
  onSubmit,
}: ModalCatalogoCodigoNombreProps) {
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [validators, setValidators] = useState<Validators>(crearValidadoresIniciales);
  const [serverError, setServerError] = useState<string | null>(null);
  const [esGuardando, setEnviando] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCodigo('');
      setNombre('');
      setValidators(crearValidadoresIniciales());
      setServerError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const manejarCambio = (field: 'codigo' | 'nombre', value: string) => {
    if (field === 'codigo') setCodigo(value);
    else setNombre(value);
    setServerError(null);
    setValidators(formValidation.updateValidators(field, value, validators));
  };

  const validarFormulario = (): boolean => {
    const result = formValidation.isFormValid(validators, { codigo, nombre });
    if (result.validators) {
      setValidators({ ...result.validators });
    }
    return result.status;
  };

  const onSubmitGuardar = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validarFormulario()) return;

    setEnviando(true);
    setServerError(null);
    try {
      await onSubmit({ codigo: codigo.trim(), nombre: nombre.trim() });
      onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'No se pudo guardar el registro');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <ModalForm
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      onSubmit={onSubmitGuardar}
      submitLabel="Guardar"
      isSubmitting={esGuardando}
      size="small"
    >
      <div className="space-y-4">
        <Input
          id="catalogo-codigo"
          label={codigoLabel}
          required
          type="text"
          autoComplete="off"
          value={codigo}
          onChange={(event) => manejarCambio('codigo', event.target.value)}
          maxLength={50}
          error={formValidation.getFieldErrors('codigo', validators)[0]}
        />
        <Input
          id="catalogo-nombre"
          label={nombreLabel}
          required
          type="text"
          autoComplete="off"
          value={nombre}
          onChange={(event) => manejarCambio('nombre', event.target.value)}
          maxLength={200}
          error={formValidation.getFieldErrors('nombre', validators)[0] || serverError || undefined}
        />
      </div>
    </ModalForm>
  );
}
